'use client';

import React, { createContext, useContext } from 'react';

// How hi-ui renders an anchor, made replaceable by the host application.
//
// hi-ui ships into three hosts: the customer portal's Perl/TT2 pages and the
// legacy presales site (both as the UMD bundle, mounted with `window.HIUI.*`),
// and the presales Next app (compiled from source). Only the Next host has a
// router, and hi-ui must never import `next` — the UMD hosts have no module
// resolver and no router to import it into. So instead of hi-ui knowing about
// any framework, the host hands it a component to render anchors WITH, and
// hi-ui renders every link through whatever it is given.
//
// The default is a plain `<a>` receiving exactly the props hi-ui already
// passed, so a host that injects nothing — customer and legacy presales, today
// and after the migration — gets byte-identical markup. That is the whole
// safety argument for this seam: absence of a provider is not a fallback path,
// it IS the original code, moved.
//
// The presales app provides a renderer backed by `next/link` for path-relative
// hrefs (the `hostBase('')` convention means same-origin links are exactly the
// path-relative ones; anything absolute points at another host and must hard
// navigate). Client-side routing then reaches the shared chrome — TopNav,
// MainNav, SectionNav, Footer — which presales-ui cannot convert on its own.
//
// This is a transitional seam of the same class as Page's `cssReset` and
// `pages.css`: it exists because hi-ui straddles a Next app and two legacy
// hosts, and it can be retired once customer lives in the monorepo. The same
// shape is the reusable answer for a future `next/image` seam.

/**
 * The anchor hi-ui wants rendered. Deliberately just the anchor's own
 * attributes — a renderer decides internal vs external from `href` alone
 * (path-relative means same-origin), so the seam carries no hi-ui concepts
 * across it and nothing has to stay in sync.
 */
export interface LinkRendererProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: React.ReactNode;
}

/** A component that renders one anchor. A component, not a function returning
 *  an element, so a host renderer is free to use hooks (a Next renderer reads
 *  the router; a future one might read the current path to mark active links). */
export type LinkRenderer = React.ComponentType<LinkRendererProps>;

const DefaultLinkRenderer: LinkRenderer = (props) => <a {...props} />;

const LinkRendererContext = createContext<LinkRenderer>(DefaultLinkRenderer);

/**
 * Renders one anchor through whatever renderer the host supplied — hi-ui's
 * plain `<a>` unless a `LinkRendererProvider` says otherwise. **This is what
 * hi-ui components render instead of a bare `<a>`.**
 *
 * It exists as a stable module-level component rather than a `useLinkRenderer()`
 * hook whose result each component renders: a component read out of context and
 * rendered as JSX is created during its parent's render, which React treats as a
 * *different* component type whenever the identity changes — remounting the
 * subtree and losing its DOM state. Ours is stable in practice, but the rule
 * (`react-hooks/static-components`) can't know that, and the pattern it warns
 * about is a real footgun for anyone injecting a renderer later. Resolving the
 * context in one fixed component removes the question entirely.
 */
export const Anchor: React.FC<LinkRendererProps> = (props) => {
  const renderer = useContext(LinkRendererContext);
  return React.createElement(renderer, props);
};

export interface LinkRendererProviderProps {
  /** Renders every anchor beneath this provider. Must be a stable reference —
   *  define it at module scope, not inline in a parent's render. */
  renderer: LinkRenderer;
  children: React.ReactNode;
}

/**
 * Replace how hi-ui renders anchors for everything beneath it. Hosts that want
 * hi-ui's default `<a>` simply don't render this — the context default already
 * is that `<a>`, which also means multiple independent mount roots (the UMD
 * hosts mount TopNav, MainNav and Footer into three separate React roots) each
 * behave correctly without any provider at all.
 */
export const LinkRendererProvider: React.FC<LinkRendererProviderProps> = ({
  renderer,
  children,
}) => <LinkRendererContext.Provider value={renderer}>{children}</LinkRendererContext.Provider>;
